import { type EmployeeTransaction } from '../../../shared/api/employee.service';
import { type CountryCode } from '../../../shared/config';
import { formatCurrency } from '../../../shared/model';

import { TipsStatementFilter } from './tips-statement-filter';
import {
  formatTipsStatementTimestamp,
  resolveTipsStatementSectionDescriptor,
  type TipsStatementSectionTitleKey,
} from './tips-statement-timestamp';

export type TipsStatementEntryKind = 'incoming' | 'withdrawal';

type TipsStatementTitleKey =
  | 'tipsStatement.entryIncoming'
  | 'tipsStatement.entryWithdrawal'
  | 'tipsStatement.entryFee'
  | 'tipsStatement.entryRefund'
  | 'tipsStatement.entryTransfer'
  | 'tipsStatement.entryTransaction';

export type TipsStatementEntry = {
  id: string;
  kind: TipsStatementEntryKind;
  titleKey: TipsStatementTitleKey;
  amount: string;
  timestamp: string;
};

export type TipsStatementSection = {
  id: string;
  title?: string;
  titleKey?: TipsStatementSectionTitleKey;
  entries: TipsStatementEntry[];
};

type SelectTipsStatementSectionsInput = {
  countryCode: CountryCode;
  filter: TipsStatementFilter;
  language: string;
  now?: Date;
  timeZone?: string;
  transactions: EmployeeTransaction[];
};

function includesFilter(
  entryKind: TipsStatementEntryKind,
  filter: TipsStatementFilter,
): boolean {
  if (filter === 'all') {
    return true;
  }

  if (filter === 'incoming') {
    return entryKind === 'incoming';
  }

  return entryKind === 'withdrawal';
}

function parseTransactionAmount(value: string | undefined): number {
  const numericValue = Number.parseFloat(value ?? '');

  if (!Number.isFinite(numericValue) || numericValue < 0) {
    return 0;
  }

  return Math.round(numericValue);
}

function resolveTipsStatementEntryKind(
  transactionType: string,
): TipsStatementEntryKind {
  // Employee.GetTransactions currently returns only user-visible tip and
  // withdrawal entries in the server flow. If the backend starts exposing
  // refund/internal-transfer rows here, the RPC contract will need an
  // explicit direction field (or from/to user role data) before the client
  // can classify them correctly.
  if (transactionType === 'tip') {
    return 'incoming';
  }

  return 'withdrawal';
}

function resolveTipsStatementEntryTitleKey(
  transactionType: string,
): TipsStatementTitleKey {
  switch (transactionType) {
    case 'tip':
      return 'tipsStatement.entryIncoming';
    case 'withdrawal':
      return 'tipsStatement.entryWithdrawal';
    case 'fee':
      return 'tipsStatement.entryFee';
    case 'refund':
      return 'tipsStatement.entryRefund';
    case 'internal_transfer':
      return 'tipsStatement.entryTransfer';
    default:
      return 'tipsStatement.entryTransaction';
  }
}

function formatTipsStatementAmount(
  transaction: EmployeeTransaction,
  kind: TipsStatementEntryKind,
  countryCode: CountryCode,
): string {
  const grossAmount = parseTransactionAmount(transaction.amount);
  const feeAmount = parseTransactionAmount(transaction.feeAmount);
  const amount =
    kind === 'incoming' ? Math.max(0, grossAmount - feeAmount) : grossAmount;
  const sign = kind === 'incoming' ? '+' : '-';

  return `${sign}${formatCurrency(amount, countryCode)}`;
}

export function selectTipsStatementSections({
  countryCode,
  filter,
  language,
  now,
  timeZone,
  transactions,
}: SelectTipsStatementSectionsInput): TipsStatementSection[] {
  const sections = new Map<string, TipsStatementSection>();

  transactions.forEach(transaction => {
    const kind = resolveTipsStatementEntryKind(transaction.type);

    if (!includesFilter(kind, filter)) {
      return;
    }

    const entryTimestampIso = transaction.completedAt ?? transaction.createdAt;
    const sectionDescriptor = resolveTipsStatementSectionDescriptor(
      language,
      entryTimestampIso,
      {
        now,
        timeZone,
      },
    );
    const section = sections.get(sectionDescriptor.id) ?? {
      id: sectionDescriptor.id,
      title: sectionDescriptor.title,
      titleKey: sectionDescriptor.titleKey,
      entries: [],
    };

    section.entries.push({
      amount: formatTipsStatementAmount(transaction, kind, countryCode),
      id: transaction.uuid,
      kind,
      timestamp: formatTipsStatementTimestamp(language, entryTimestampIso, {
        timeZone,
      }),
      titleKey: resolveTipsStatementEntryTitleKey(transaction.type),
    });
    sections.set(sectionDescriptor.id, section);
  });

  return Array.from(sections.values());
}
